/**
 * State 03 — Sign in: Forgot password.
 *
 * Single email field, primary CTA "Send reset code", secondary
 * "Back to sign in". On submit, calls
 * `signIn.create({ strategy: "reset_password_email_code", identifier })`
 * then advances to state 04 (resetPassword) where the user enters the
 * 6-digit code and new password.
 *
 * Verbatim copy from C:/tmp/mio-auth-journey.html state 03.
 */

"use client";

import { FormEvent, useState } from "react";

import { useSignIn } from "@clerk/nextjs";

import { IconWarningTriangle } from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import {
  AuthFlowState,
  AuthStep,
  readClerkError,
} from "./useAuthFlow";

type Props = {
  flow: AuthFlowState;
  setIdentifier: (v: string) => void;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  markResend: () => void;
  goTo: (s: AuthStep) => void;
};

export function SignInForgotPasswordCard({
  flow,
  setIdentifier,
  setError,
  setSubmitting,
  markResend,
  goTo,
}: Props) {
  const { signIn, isLoaded } = useSignIn();
  const [email, setEmail] = useState(flow.identifier);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
      setIdentifier(email);
      markResend();
      goTo("signIn.resetPassword");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="mio-auth-card-header">
        <h1>Reset your password</h1>
        <p>We&rsquo;ll email you a 6-digit code to set a new one.</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-fp-email">
            Email
          </label>
        </div>
        <input
          id="mio-fp-email"
          className="mio-auth-input"
          type="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={flow.submitting || email.length === 0}
      >
        {flow.submitting ? (
          <>
            Sending
            <EllipsisDots />
          </>
        ) : (
          "Send reset code"
        )}
      </button>
      <button
        type="button"
        className="mio-auth-secondary"
        onClick={() => goTo("signIn.start")}
      >
        Back to sign in
      </button>
    </form>
  );
}
